import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  Send, 
  Phone, 
  MapPin, 
  Camera, 
  FileText, 
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  Menu,
  Home,
  User,
  Settings
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { Department, ComplaintCategory } from '@/types';

interface WhatsAppMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
  attachments?: {
    type: 'image' | 'document';
    url: string;
    name?: string;
  }[];
}

interface WhatsAppBotProps {
  isOpen: boolean;
  onClose: () => void;
}

const WhatsAppBot: React.FC<WhatsAppBotProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [currentStep, setCurrentStep] = useState<'welcome' | 'phone_verification' | 'department_selection' | 'complaint_details' | 'confirmation'>('welcome');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<ComplaintCategory[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ComplaintCategory | null>(null);
  const [complaintDetails, setComplaintDetails] = useState({
    summary: '',
    description: '',
    location: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchDepartments();
      fetchCategories();
      initializeBot();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeBot = () => {
    const welcomeMessage: WhatsAppMessage = {
      id: '1',
      type: 'bot',
      content: `👋 Hello! Welcome to NFCRWA- NEW FRIENDS COLONY RESIDENTS WELFARE ASSOCIATION Complaint System.\n\nI'm here to help you register a complaint quickly and easily. Let's get started!`,
      timestamp: new Date()
    };
    setMessages([welcomeMessage]);
    setCurrentStep('phone_verification');
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/v1/departments', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/v1/complaint-categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const addMessage = (content: string, type: 'user' | 'bot' = 'bot', isTyping = false) => {
    const newMessage: WhatsAppMessage = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: new Date(),
      isTyping
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const simulateTyping = (message: string, delay = 1000) => {
    setIsLoading(true);
    addMessage('', 'bot', true);
    
    setTimeout(() => {
      setMessages(prev => prev.slice(0, -1));
      addMessage(message);
      setIsLoading(false);
    }, delay);
  };

  const handlePhoneVerification = async (phone: string) => {
    if (!phone.match(/^\d{10}$/)) {
      addMessage('Please enter a valid 10-digit mobile number.');
      return;
    }

    setPhoneNumber(phone);
    addMessage(phone, 'user');
    
    // Simulate phone verification
    simulateTyping('📱 Verifying your mobile number...', 1500);
    
    setTimeout(() => {
      simulateTyping('✅ Mobile number verified! Now let\'s select the department for your complaint.', 1000);
      setCurrentStep('department_selection');
    }, 2000);
  };

  const handleDepartmentSelection = (department: Department) => {
    setSelectedDepartment(department);
    addMessage(`Selected: ${department.name}`, 'user');
    
    simulateTyping(`Great! You've selected ${department.name}. Now let's get the details of your complaint.`, 1000);
    setCurrentStep('complaint_details');
  };

  const handleCategorySelection = (category: ComplaintCategory) => {
    setSelectedCategory(category);
    addMessage(`Category: ${category.name}`, 'user');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    
    files.forEach(file => {
      addMessage(`📎 Attached: ${file.name}`, 'user');
    });
  };

  const handleSubmitComplaint = async () => {
    if (!complaintDetails.summary.trim()) {
      addMessage('Please provide a summary of your complaint.');
      return;
    }

    setIsLoading(true);
    addMessage('Submitting your complaint...', 'user');
    addMessage('', 'bot', true);

    try {
      const formData = new FormData();
      formData.append('summary', complaintDetails.summary);
      formData.append('description', complaintDetails.description);
      formData.append('location', complaintDetails.location);
      formData.append('priority', complaintDetails.priority);
      formData.append('department_id', selectedDepartment?.id || '');
      formData.append('category_id', selectedCategory?.id || '');
      formData.append('phone_number', phoneNumber);
      
      attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
      });

      const response = await fetch('/api/v1/complaints', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => prev.slice(0, -1));
        addMessage(`✅ Complaint submitted successfully!\n\nComplaint ID: #${data.data.complaint_number}\n\nYou will receive updates via WhatsApp. Thank you!`);
        setCurrentStep('confirmation');
      } else {
        const errorData = await response.json();
        setMessages(prev => prev.slice(0, -1));
        addMessage(`❌ Error: ${errorData.message || 'Failed to submit complaint. Please try again.'}`);
      }
    } catch (error) {
      setMessages(prev => prev.slice(0, -1));
      addMessage('❌ Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const message = inputMessage.trim();
    setInputMessage('');

    switch (currentStep) {
      case 'phone_verification':
        handlePhoneVerification(message);
        break;
      case 'complaint_details':
        if (message.toLowerCase().includes('summary:')) {
          setComplaintDetails(prev => ({ ...prev, summary: message.replace('summary:', '').trim() }));
          addMessage(`Summary: ${message.replace('summary:', '').trim()}`, 'user');
          simulateTyping('Got it! Please provide a detailed description of the issue.');
        } else if (message.toLowerCase().includes('description:')) {
          setComplaintDetails(prev => ({ ...prev, description: message.replace('description:', '').trim() }));
          addMessage(`Description: ${message.replace('description:', '').trim()}`, 'user');
          simulateTyping('Perfect! Any specific location details?');
        } else if (message.toLowerCase().includes('location:')) {
          setComplaintDetails(prev => ({ ...prev, location: message.replace('location:', '').trim() }));
          addMessage(`Location: ${message.replace('location:', '').trim()}`, 'user');
          simulateTyping('Excellent! Ready to submit your complaint? Type "submit" to proceed.');
        } else if (message.toLowerCase() === 'submit') {
          handleSubmitComplaint();
        } else {
          addMessage(message, 'user');
          simulateTyping('Please use the format:\n- summary: [your summary]\n- description: [detailed description]\n- location: [specific location]\n- submit (when ready)');
        }
        break;
      default:
        addMessage(message, 'user');
    }
  };

  const renderDepartmentButtons = () => (
    <div className="space-y-2">
      {departments.map((dept) => (
        <button
          key={dept.id}
          onClick={() => handleDepartmentSelection(dept)}
          className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
        >
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
              {dept.name.charAt(0)}
            </div>
            <div>
              <div className="font-medium text-gray-900">{dept.name}</div>
              <div className="text-sm text-gray-600">{dept.description}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );

  const renderCategoryButtons = () => (
    <div className="space-y-2">
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => handleCategorySelection(category)}
          className={`w-full text-left p-3 rounded-lg border transition-colors ${
            selectedCategory?.id === category.id
              ? 'bg-green-50 border-green-200'
              : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
          }`}
        >
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
              {category.name.charAt(0)}
            </div>
            <div>
              <div className="font-medium text-gray-900">{category.name}</div>
              <div className="text-sm text-gray-600">{category.description}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 'phone_verification':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <Phone className="mx-auto h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Verify Your Mobile Number</h3>
              <p className="text-sm text-gray-600">Please enter your 10-digit mobile number to continue</p>
            </div>
          </div>
        );

      case 'department_selection':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <Home className="mx-auto h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select Department</h3>
              <p className="text-sm text-gray-600">Which department should handle your complaint?</p>
            </div>
            {renderDepartmentButtons()}
          </div>
        );

      case 'complaint_details':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <FileText className="mx-auto h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Complaint Details</h3>
              <p className="text-sm text-gray-600">Please provide details about your complaint</p>
            </div>
            
            {categories.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Select Category (Optional)</h4>
                {renderCategoryButtons()}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={complaintDetails.priority}
                  onChange={(e) => setComplaintDetails(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Attach Files (Optional)</label>
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((file, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-600">
                      <FileText className="h-4 w-4 mr-2" />
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'confirmation':
        return (
          <div className="text-center space-y-4">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
            <h3 className="text-lg font-medium text-gray-900">Complaint Submitted Successfully!</h3>
            <p className="text-sm text-gray-600">You will receive updates via WhatsApp</p>
            <button
              onClick={() => {
                setCurrentStep('welcome');
                setMessages([]);
                setPhoneNumber('');
                setSelectedDepartment(null);
                setSelectedCategory(null);
                setComplaintDetails({ summary: '', description: '', location: '', priority: 'medium' });
                setAttachments([]);
                initializeBot();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Submit Another Complaint
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      <div className="absolute right-4 bottom-4 w-96 h-[600px] bg-white rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-green-500 text-white rounded-t-lg">
          <div className="flex items-center">
            <MessageCircle className="h-6 w-6 mr-2" />
            <div>
              <h3 className="font-medium">NFCRWA Support</h3>
              <p className="text-xs opacity-90">Complaint Registration</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs px-4 py-2 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {message.isTyping ? (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">{message.content}</div>
                )}
                <div className={`text-xs mt-1 ${
                  message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
          
          {currentStep !== 'confirmation' && (
            <div className="space-y-4">
              {renderStepContent()}
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {currentStep !== 'confirmation' && (
          <div className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppBot;
